/**
 * The two things Wrapped was saying in the database's voice, said in a person's instead.
 *
 * A wrap's `status` is a column — `sealed`, `peeling`, `withdrawn` — and it was being printed
 * straight onto the Sent card's badge, so the giver read "peeling" about a photograph. `hold_until`
 * is an ISO instant and it was landing whole in the middle of a sentence about a present. Both are
 * correct and neither is English, and both are read in more than one place, so the mapping lives
 * here rather than as a ternary at each call site: a second copy of a table like this is how one
 * screen ends up calling the same state two different names.
 */

/**
 * Every status a wrap can be in, as the word for it.
 *
 * `vanished` is here even though the Sent query filters it out — a table whose fallback is the raw
 * column name only ever fails silently, and the one status the query is meant to exclude is
 * exactly the one worth having an answer for if it ever slips through.
 */
export const WRAP_STATUS_WORDS: Record<string, string> = {
  draft: 'Not sent',
  sealed: 'Waiting',
  peeling: 'Being opened',
  opened: 'Opened',
  withdrawn: 'Taken back',
  expired: 'Expired',
  vanished: 'Gone',
};

export const statusWord = (status: string): string => WRAP_STATUS_WORDS[status] ?? status;

/**
 * Long weekday names, hand-held for the same reason `day.ts` hand-holds the short ones: `Intl` is
 * present on the web and on modern Hermes builds and absent on older ones, and a hold that renders
 * as "until Sunday" on a laptop and throws on somebody's phone is the worst of the three outcomes.
 */
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * WHEN something happens, as somebody would say it: `tomorrow evening`, `Sunday`, `25 Dec`.
 *
 * A hold is twenty-four hours, so the honest answer is nearly always "tomorrow" and some part of
 * the day — which is what a person needs to plan around, and is all the precision a stay of
 * execution on a photograph has ever wanted. Naming the hour would be a countdown, and a countdown
 * is the thing the copy rules will not let this app put on a gift.
 *
 * Past two days the part of the day stops helping and the weekday takes over, because "was that a
 * weekend?" is the next question after "when". `day.ts` deliberately does not do this: it labels a
 * calendar DAY that has already happened, and this labels an INSTANT that has not.
 */
export function whenLabel(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(at) - midnight(now)) / 86_400_000);
  const hour = at.getHours();
  const part = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : hour < 22 ? 'evening' : 'night';

  // A moment already past is never shown as one — a hold is only reported while it is holding —
  // so the first branch is "today", and it answers for anything earlier too rather than inventing
  // a tense the caller has no use for.
  if (days <= 0) return `this ${part}`;
  if (days === 1) return `tomorrow ${part}`;
  if (days < 7) return WEEKDAYS[at.getDay()]!;
  return `${at.getDate()} ${MONTHS[at.getMonth()]}`;
}
